import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { X, ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useNpsPromptDismissed, resolveNpsPrompt } from '@/hooks/useNpsPrompt';

interface NPSInlineWidgetProps {
  /** Identificador único del disparador, ej. `cohort_last_week:${cohortId}` */
  triggerKey: string;
  cohortId: string | null;
  title?: string;
  className?: string;
}

// Widget inline (no modal) de encuesta NPS. Se cierra con la X y no vuelve a
// aparecer para ese triggerKey (persistido en nps_prompt_dismissals), sin
// importar si el usuario respondió o solo lo descartó.
export function NPSInlineWidget({ triggerKey, cohortId, title, className }: NPSInlineWidgetProps) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { data: isDismissed, isLoading } = useNpsPromptDismissed(triggerKey);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolve = async (submittedScore: number | null) => {
    if (!profile?.id) return;
    setIsSubmitting(true);
    try {
      await resolveNpsPrompt({
        userId: profile.id,
        triggerKey,
        cohortId,
        score: submittedScore,
        comment: submittedScore !== null ? comment : null,
      });
      if (submittedScore !== null) toast.success('¡Gracias por tu feedback!');
      queryClient.invalidateQueries({ queryKey: ['nps-prompt-dismissed', profile.id, triggerKey] });
      queryClient.invalidateQueries({ queryKey: ['nps-responses', profile.id] });
    } catch (err: any) {
      toast.error('No se pudo guardar', { description: err?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Evita el flash mientras carga, y se oculta si ya se resolvió antes.
  if (isLoading || isDismissed) return null;

  return (
    <Card className={cn('glass-card border-primary/40 relative', className)}>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={() => resolve(null)}
        disabled={isSubmitting}
        aria-label="Cerrar encuesta"
      >
        <X className="w-4 h-4" />
      </Button>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2 pr-6">
          <ThumbsUp className="w-5 h-5 text-primary shrink-0" />
          <p className="font-medium text-sm">
            {title || '¿Qué tan probable es que recomiendes este programa a un colega?'}
          </p>
        </div>

        <div className="grid grid-cols-11 gap-1">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
            <button
              key={value}
              onClick={() => setScore(value)}
              disabled={isSubmitting}
              className={cn(
                'h-8 rounded-md font-semibold text-xs transition-all',
                score === value
                  ? value <= 6
                    ? 'bg-destructive text-destructive-foreground ring-2 ring-ring ring-offset-1'
                    : value <= 8
                      ? 'bg-amber-500 text-white ring-2 ring-ring ring-offset-1'
                      : 'bg-emerald-500 text-white ring-2 ring-ring ring-offset-1'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground',
              )}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground px-0.5 -mt-2">
          <span>Nada probable</span>
          <span>Muy probable</span>
        </div>

        {score !== null && (
          <Textarea
            placeholder="¿Qué podríamos mejorar? (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="resize-none text-sm"
            maxLength={1000}
          />
        )}

        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => resolve(score)}
            disabled={score === null || isSubmitting}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
